import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Drawer, Spin } from "antd";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";

interface LocationPoint {
  Latitude: number | string;
  Longitude: number | string;
}
interface MapCoordinates {
  lat: number;
  lng: number;
}

interface RouteMapDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  route?: LocationPoint[];
  isMapLoaded: boolean;
  width?: number;
  clampToIndiaBox?: boolean;
}

// ✅ CSSProperties (NOT MapOptions)
const containerStyle: React.CSSProperties = {
  width: "100%",
  height: "500px",
};

const mapOptions: google.maps.MapOptions = {
  // e.g. disableDefaultUI: true, zoomControl: true
};

const INDIA_LAT_MIN = 6;
const INDIA_LAT_MAX = 37;
const INDIA_LNG_MIN = 68;
const INDIA_LNG_MAX = 97;

const RouteMapDrawer: React.FC<RouteMapDrawerProps> = ({
  isOpen,
  onClose,
  route,
  isMapLoaded,
  width = 860,
  clampToIndiaBox = true,
}) => {
  const employeeTrackingList = useSelector(
    (state: any) => state.employee.employeeTrackingList
  );

  const sourceRoute: LocationPoint[] = useMemo(() => {
    if (Array.isArray(route) && route.length) return route;
    return Array.isArray(employeeTrackingList) ? employeeTrackingList : [];
  }, [route, employeeTrackingList]);

  const cleanedRoute: MapCoordinates[] = useMemo(() => {
    const points = (sourceRoute || [])
      .filter((p: any) => p?.Latitude && p?.Longitude)
      .map((p: any) => ({ lat: Number(p.Latitude), lng: Number(p.Longitude) }))
      .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));

    if (!clampToIndiaBox) return points;
    return points.filter(
      (p) =>
        p.lat >= INDIA_LAT_MIN &&
        p.lat <= INDIA_LAT_MAX &&
        p.lng >= INDIA_LNG_MIN &&
        p.lng <= INDIA_LNG_MAX
    );
  }, [sourceRoute, clampToIndiaBox]);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [tilesLoaded, setTilesLoaded] = useState<boolean>(false);
  const [mapReady, setMapReady] = useState<boolean>(false);

  // Remount map every time Drawer opens (and make a fresh instance)
  const [mapKey, setMapKey] = useState(0);
  useEffect(() => {
    if (isOpen) {
      setTilesLoaded(false);
      setMapReady(false);
      setMapKey((k) => k + 1);
    } else {
      // clear map reference when closing to avoid stale instance
      setMap(null);
    }
  }, [isOpen]);

  const fitRouteBounds = useCallback(
    (mapInstance: google.maps.Map, pts: MapCoordinates[]) => {
      if (!pts.length) return;
      const bounds = new window.google.maps.LatLngBounds();
      pts.forEach((pt) => bounds.extend(pt));
      mapInstance.fitBounds(bounds);
      if (pts.length === 1) {
        mapInstance.setCenter(pts[0]);
        mapInstance.setZoom(15);
      }
    },
    []
  );

  // A safe “ensure map sees final size” method when drawer opens
  const resizeSoon = useCallback(() => {
    if (!map) return;
    // run after layout settles
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.google.maps.event.trigger(map, "resize");
        fitRouteBounds(map, cleanedRoute);
      });
    });
  }, [map, cleanedRoute, fitRouteBounds]);

  const polylineOptions = useMemo<google.maps.PolylineOptions>(
    () => ({
      strokeColor: "#FF0000",
      strokeOpacity: 0.8,
      strokeWeight: 4,
      geodesic: true,
    }),
    []
  );

  return (
    <Drawer
      title="Employee Route"
      placement="right"
      width={width}
      open={isOpen}
      onClose={onClose}
      destroyOnClose={true}
      style={{ marginTop: 50, paddingBottom: 50 }}

      afterOpenChange={(open) => {
        if (open) {
          setTilesLoaded(false);
          resizeSoon();
        } else {
          setMapReady(false);
          setTilesLoaded(false);
        }
      }}
    >
      {!isMapLoaded ? (
        <div className="flex items-center justify-center" style={{ height: 500 }}>
          <Spin size="large" tip="Loading map library..." />
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          {!tilesLoaded && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                background: "rgba(255,255,255,0.65)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spin size="large" tip={mapReady ? "Loading map tiles..." : "Preparing map..."} />
            </div>
          )}

          <GoogleMap
            key={mapKey}
            mapContainerStyle={containerStyle}
            options={mapOptions}
            center={
              cleanedRoute.length
                ? cleanedRoute[Math.floor(cleanedRoute.length / 2)]
                : { lat: 21.1458, lng: 79.0882 }
            }
            zoom={5}
            onLoad={(mapInstance) => {
              setMap(mapInstance);
              setMapReady(true);

              // Wait until first IDLE (tiles/projection ready), then fit.
              // Using addListenerOnce is the most reliable way for 2nd+ openings.
              const onceIdle = window.google.maps.event.addListenerOnce(
                mapInstance,
                "idle",
                () => {
                  fitRouteBounds(mapInstance, cleanedRoute);
                  setTilesLoaded(true);
                }
              );

              // Also schedule a resize shortly after load in case Drawer is still animating
              resizeSoon();

              // If you want to be extra-safe, you can clean the one-time listener on unmount:
              // (React-unmount will do this automatically with the map instance disposal,
              // but it's fine to keep this note.)
            }}
            // Fallback: if idle fires before onLoad finishes, this will still flip spinner.
            onIdle={() => setTilesLoaded(true)}
          >
            {cleanedRoute.length > 0 && (
              <>
                <Marker
                  position={cleanedRoute[0]}
                  label={{
                    text: "Start",
                    color: "black",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                  icon={{ url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png" }}
                />
                <Marker
                  position={cleanedRoute[cleanedRoute.length - 1]}
                  label={{
                    text: "End",
                    color: "black",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                  icon={{ url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png" }}
                />
                <Polyline path={cleanedRoute} options={polylineOptions} />
              </>
            )}
          </GoogleMap>

          {cleanedRoute.length === 0 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
                zIndex: 1,
                pointerEvents: "none",
              }}
            >
              No valid route points to display.
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default RouteMapDrawer;
